import React, { useEffect, useState } from "react";
import { Instagram, Facebook, Youtube, Linkedin } from "lucide-react";

const Completion: React.FC = () => {
  const [questionId, setQuestionId] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    // Get question ID from localStorage before clearing
    const storedQuestionId = localStorage.getItem("questionId");
    if (storedQuestionId) {
      setQuestionId(storedQuestionId);
    }

    localStorage.clear();
    sessionStorage.clear();
  }, []);

  // Add this useEffect at the top of your other useEffects
  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, []);

  const copyToClipboard = async () => {
    const link = `${window.location.origin}/listening/${questionId}`;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy: ", err);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-red-200 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="bg-gradient-to-r from-red-500 to-red-600 p-6 text-white text-center">
          <h1 className="text-2xl font-bold">
            🎉 Thank You for Completing the Final Round! 🎉
          </h1>
        </div>

        <div className="p-6 space-y-6">
          <div className="space-y-3">
            <p className="flex items-center text-green-600 font-medium">
              <span className="mr-2 text-lg">✅</span> Your responses have been
              successfully submitted.
            </p>
            <p className="flex items-center text-blue-600 font-medium">
              <span className="mr-2 text-lg">📌</span> Please wait for the
              results to know if you are selected for the next step.
            </p>
          </div>

          <div className="space-y-3">
            <h2 className="text-xl font-semibold flex items-center">
              <span className="mr-2 text-lg">📢</span> What's Next?
            </h2>
            <p className="text-gray-700 ml-6">
              Our team will review your performance and contact you if you are
              selected. Please stay available for updates.
            </p>
          </div>

          {questionId && (
            <div className="space-y-3">
              <h2 className="text-xl font-semibold flex items-center">
                <span className="mr-2 text-lg">🔗</span> Listening Session Link
              </h2>
              <div className="ml-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-gray-700 mb-2">
                  Access your listening session:
                </p>
                <div className="flex items-center gap-2 mb-2">
                  <a
                    href={`${window.location.origin}/listening/${questionId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 underline break-all font-mono text-sm flex-1"
                  >
                    {window.location.origin}/listening/{questionId}
                  </a>
                  <button
                    onClick={copyToClipboard}
                    className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                    title="Copy to clipboard"
                  >
                    {copied ? (
                      <span className="text-green-600">✓</span>
                    ) : (
                      <span className="text-lg">📋</span>
                    )}
                  </button>
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  Click the link above to access your listening session
                </p>
              </div>
            </div>
          )}

          <div className="space-y-3">
            <h2 className="text-xl font-semibold flex items-center">
              <span className="mr-2 text-lg">📞</span> For any queries, contact:
            </h2>
            <ul className="ml-6 space-y-2 text-gray-700">
              <li className="flex items-center">
                +91 9071001353 (Harshitha San)
              </li>
              <li className="flex items-center">
                +91 9071002349 (Swaroop San)
              </li>
              <li className="flex items-center">
                +91 9071002350 (Nandidni San)
              </li>
            </ul>
          </div>

          <div className="pt-4 border-t border-gray-200">
            <h2 className="text-xl font-semibold mb-4">Connect With Us</h2>
            <div className="flex space-x-4 justify-center">
              <a
                href="https://www.instagram.com/navishumanresources/"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 bg-pink-600 text-white rounded-full hover:bg-pink-700 transition-colors"
              >
                <Instagram size={24} />
              </a>
              <a
                href="https://www.facebook.com/NAVISHROFFICIAL/"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors"
              >
                <Facebook size={24} />
              </a>
              <a
                href="https://www.youtube.com/@navishumanresources"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors"
              >
                <Youtube size={24} />
              </a>
              <a
                href="https://www.linkedin.com/company/navishumanresources/"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 bg-blue-700 text-white rounded-full hover:bg-blue-800 transition-colors"
              >
                <Linkedin size={24} />
              </a>
            </div>
          </div>

          <div className="text-center mt-6">
            <p className="text-lg font-bold text-blue-600">
              🚀 All the best! We appreciate your time and effort. 😊
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Completion;
